import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiSave, FiGlobe, FiMail, FiPackage } from 'react-icons/fi';
import superadminService from '../../../services/superadminService';
import { Tenant, TenantUpdate, PlanType } from '../../../types/tenant';
import { useNotification } from '../../../contexts/NotificationContext';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  
  h2 {
    font-size: 2rem;
    color: #333;
    margin: 0;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
    border-color: #ccc;
  }
  
  svg {
    font-size: 1.25rem;
    color: #666;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1.5rem 0;
  color: #333;
  font-size: 1.25rem;
  
  svg {
    color: #ff6b6b;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #555;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s;
  background-color: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const Switch = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  
  input {
    width: 1.25rem;
    height: 1.25rem;
    cursor: pointer;
  }
  
  span {
    font-size: 0.875rem;
    color: #666;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #ff6b6b;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #ff5252;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: white;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.2rem;
  color: #666;
`;

const EditTenant: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    plan_type: 'basic' as PlanType,
    billing_email: '',
    is_active: true
  });

  useEffect(() => {
    loadTenant();
  }, [tenantId]);

  const loadTenant = async () => {
    if (!tenantId) return;
    
    try {
      setLoadingTenant(true);
      const data = await superadminService.getTenant(tenantId);
      setTenant(data);
      setFormData({
        name: data.name,
        plan_type: data.plan_type,
        billing_email: data.billing_email || '',
        is_active: data.is_active
      });
    } catch (error) {
      showNotification('Erro ao carregar tenant', 'error');
      navigate('/superadmin/tenants');
    } finally {
      setLoadingTenant(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenantId || !tenant) return;

    try {
      setLoading(true);
      
      const updateData: TenantUpdate = {
        name: formData.name,
        plan_type: formData.plan_type,
        billing_email: formData.billing_email || undefined,
        is_active: formData.is_active
      };

      await superadminService.updateTenant(tenantId, updateData);
      
      showNotification('Tenant atualizado com sucesso!', 'success');
      navigate('/superadmin/tenants');
    } catch (error: any) {
      showNotification(
        error.response?.data?.detail || 'Erro ao atualizar tenant',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loadingTenant) {
    return <LoadingContainer>Carregando...</LoadingContainer>;
  }

  if (!tenant) {
    return <LoadingContainer>Tenant não encontrado</LoadingContainer>;
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/superadmin/tenants')}>
          <FiArrowLeft />
        </BackButton>
        <h2>Editar Tenant</h2>
      </Header>

      <Form onSubmit={handleSubmit}>
        <Card>
          <CardTitle>
            <FiPackage />
            Informações Básicas
          </CardTitle>
          
          <FormGroup>
            <Label htmlFor="slug">Slug (identificador único)</Label>
            <Input
              type="text"
              id="slug"
              value={tenant.slug}
              disabled
              title="O slug não pode ser alterado"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="name">Nome do Tenant *</Label>
            <Input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              placeholder="Ex: Imobiliária Central"
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="plan_type">Plano *</Label>
            <Select
              id="plan_type"
              value={formData.plan_type}
              onChange={(e) => handleInputChange('plan_type', e.target.value)}
              required
            >
              <option value="trial">Trial (14 dias)</option>
              <option value="basic">Básico</option>
              <option value="professional">Profissional</option>
              <option value="enterprise">Enterprise</option>
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="billing_email">Email de Cobrança</Label>
            <Input
              type="email"
              id="billing_email"
              value={formData.billing_email}
              onChange={(e) => handleInputChange('billing_email', e.target.value)}
              placeholder="financeiro@empresa.com.br"
            />
          </FormGroup>
          
          <Switch>
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
            />
            <span>Tenant ativo</span>
          </Switch>
        </Card>

        <Card>
          <CardTitle>
            <FiGlobe />
            Domínio Atual
          </CardTitle>
          
          <FormGroup>
            <Label>Domínio Principal</Label>
            <Input
              type="text"
              value={tenant.domain || tenant.subdomain || 'Não configurado'}
              disabled
              title="Para alterar o domínio, use a página de configurações"
            />
            <small style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.25rem' }}>
              Para configurar domínios, use a página de Configurações do tenant
            </small>
          </FormGroup>
        </Card>

        <Actions>
          <CancelButton type="button" onClick={() => navigate('/superadmin/tenants')}>
            Cancelar
          </CancelButton>
          
          <SaveButton type="submit" disabled={loading}>
            <FiSave />
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </SaveButton>
        </Actions>
      </Form>
    </Container>
  );
};

export default EditTenant;